import {
    IEmbedConfig,
    IJWPAdvertising,
    IJWPlayerConfig,
    IJWPSponsorshipAdTargeting,
    IJWPVideoAdTargeting,
    JWPlayerConfigEvent,
    VideoEventMap
} from '../types'
import { JWQualityLevel, IJWMedia } from '../types'
import { AbstractPlayer } from './abstract-player'
import { ScriptService } from 'services/script/script.service'
import { getEmbedConfig } from '../util'
import { ObjectUtils } from 'utils/object'
import { logger } from 'utils/logger'

// From Analytics
const JW_INTERACTION_PLAY_REASON = ['interaction', 'related-interaction']
enum State {
    NULL = '',
    PAUSE = 'pause',
    COMPLETE = 'complete'
}

export class JWPlayer extends AbstractPlayer {
    protected player: jwplayer.JWPlayer

    // From Analytics
    private playStarted = false
    private previousState: State = State.NULL

    get index(): number {
        return this.player.getPlaylistIndex()
    }
    destroy(): void {
        this.player.remove()
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    cuePlaylist(_: YT.IPlaylistSettings): void {}

    public constructor(
        protected element: HTMLElement,
        protected videoId: string,
        protected events: JWPlayerConfigEvent = {},
        // From Analytics
        protected isAutoPlay: boolean = false,
        protected isPlaylist: boolean = false,
        private version: string
    ) {
        super(element, videoId, {}, events, isAutoPlay, isPlaylist)
        this.featured = this.element.dataset.type === 'featured'
        this.iframeData = JSON.parse(this.element.dataset.evtVal || '{}')
    }

    /**
     * When the user meets the element on their screen,
     * load script and initialize the player.
     */
    public render(): void {
        ScriptService.getInstance()
            .loadScript({
                name: `jwplayer${this.version}`,
                src: `https://cdn.jwplayer.com/libraries/${this.version}.js`,
                loaded: false
            })
            .then((result) => {
                if (result.loaded) {
                    this.init().catch((err) => logger('warn', err))
                    if (this.events.onRender) {
                        this.events.onRender()
                    }
                }
            })
            .catch((error: Error) =>
                logger('error', `JW ${this.version} : ${error}`)
            )
    }

    public async init(): Promise<void> {
        const videoData = await this.getJWMediaMetadata(this.videoId)
        // Use proper media object API endpoint
        // https://developer.jwplayer.com/jwplayer/reference/playlists
        // https://developer.jwplayer.com/jwplayer/reference/media
        const mediaEndpoint = this.isPlaylist ? 'playlists' : 'media'
        let config: IJWPlayerConfig = {
            playlist: `//cdn.jwplayer.com/v2/${mediaEndpoint}/${this.videoId}`,
            width: '100%',
            aspectratio: '16:9',
            autostart: false
        }

        if (this.isMonetize(videoData)) {
            const embedConfig: IEmbedConfig = await getEmbedConfig()
            if (Object.keys(embedConfig).length) {
                config = {
                    ...config,
                    advertising: this.getAdvertisingConfigData(embedConfig)
                }
            }
        }

        this.player = window.jwplayer(this.element)

        if (!Object.prototype.hasOwnProperty.call(this.player, 'setup')) {
            return Promise.reject()
        }

        this.player.setup(config)

        this.player.on('ready', () => {
            this.onReady()
            if (this.events.onReady) {
                this.events.onReady()
            }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.player.on('play', (event: any) => {
            this.onPlay(event)
            if (this.events.onPlay) {
                this.events.onPlay(event)
            }
        })
        this.player.on('playlistItem', (event: jwplayer.PlaylistItemParam) =>
            this.onPlaylistItem(event)
        )
        this.player.on('mute', (event: jwplayer.MuteParam) =>
            this.onMute(event)
        )
        this.player.on('complete', () => this.onComplete())
        this.player.on('pause', () => this.onPause())
        this.player.on('error', (event: jwplayer.ErrorParam) => {
            this.onError(event)
            if (this.events.onError) {
                this.events.onError(event)
            }
        })
        this.player.on('adPlay', (event: jwplayer.AdPlayParam) =>
            this.onAdPlay(event)
        )
        this.player.on('adComplete', () => this.onAdEnd())
        this.player.on('adSkipped', () => this.onAdEnd())
        this.player.on('setupError', (event) => {
            this.onError(event)
            if (this.events.onError) {
                this.events.onError(event)
            }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.player.on('visualQuality', (event: any) =>
            this.onQualityChange(event)
        )
        return Promise.resolve()
    }

    protected onReady(): void {
        this.isVolumeEnabled = !this.player.getMute()
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
    private onPlay(event: any): void {
        this.pauseAllOther()
        const qualityLevel = (this.player.getVisualQuality() as JWQualityLevel) || {
            level: { index: 0 }
        }
        this.playbackQuality = this.getVisualQuality(qualityLevel.level.index)

        if (!this.playStarted && this.previousState != event.playReason) {
            const isNonInteraction = !JW_INTERACTION_PLAY_REASON.includes(
                event.playReason
            )
                ? true
                : false
            // keep same autPlay state if previousState is pause. Otherwise, isAutoPlay same as isNonInteraction
            if (this.previousState !== State.PAUSE) {
                this.isAutoPlay = isNonInteraction
            }
            this.previousState = event.reason
            this.dispatchAnalyticsPlay(isNonInteraction)
        }
        this.playStarted = true
    }

    private onPlaylistItem(event: jwplayer.PlaylistItemParam): void {
        if (event.item && event.item.mediaid) {
            this.videoId = event.item['mediaid']
            this.videoTitle = event.item['title']
            this.resetFlags()
        }

        const eventC = new CustomEvent(VideoEventMap.JW_SKIP, {
            detail: event.item
        })
        window.dispatchEvent(eventC)
    }

    private onMute(event: jwplayer.MuteParam): void {
        this.isVolumeEnabled = !event.mute
    }

    private onComplete(): void {
        this.playStarted = false
        this.previousState = State.COMPLETE
        this.dispatchAnalyticsComplete()
    }

    private onPause(): void {
        this.playStarted = false
        this.previousState = State.PAUSE
        this.dispatchAnalyticsPaused()
    }

    private onAdPlay(event: jwplayer.AdPlayParam): void {
        logger(
            'warn',
            'Fired whenever an ad starts playing or when an ad is unpaused.',
            event
        )
        this.pauseAllOther()
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
    private onQualityChange(event: any): void {
        this.playbackQuality = this.getVisualQuality(event.level.index)
    }

    public get title(): string {
        return this.player.getPlaylistItem()['title']
    }

    public pause(): void {
        this.player.pause()
    }
    public play(): void {
        this.player.play()
    }
    public playVideoAt(index: number): void {
        this.player.playlistItem(index)
    }
    public autoPlay(): void {
        this.mute()
        this.play()
    }
    public mute(): void {
        this.player.setMute(true)
    }
    public unmute(): void {
        this.player.setMute(false)
    }
    public isPausable(): boolean {
        return this.isAutoPlay
    }

    /**
     * get Current Visual Quality
     */
    private getVisualQuality(index = 0): string {
        const levels = this.player.getQualityLevels()
        return levels[index].label
    }

    private getAdvertisingConfigData(
        adTargets: IEmbedConfig,
        adScheduleId?: string
    ): IJWPAdvertising {
        const alphaNumericGenLength = 8
        /* eslint-disable no-magic-numbers */
        const playerAdSizes = [
            [640, 480],
            [480, 360],
            [480, 361]
        ]
        /* eslint-enable no-magic-numbers */
        const playerAdscheduleId = ObjectUtils.getAlphaNumericRandomGen(
            alphaNumericGenLength
        )

        // Video targeting params from FEM
        const custParams =
            adTargets.adsConfig?.adTagParameters?.cust_params || ''
        const iu = adTargets.adsConfig?.adTagParameters?.iu || ''
        const ppid = adTargets.adsConfig?.adTagParameters?.ppid || ''

        // Custom params for sponsorship support
        const sponsorshipAdTag: IJWPSponsorshipAdTargeting = {
            sensitive_video: '__item-sensitive_video__',
            vid: '__item-mediaid__'
        }

        const custSponsorshipParams = `%26${ObjectUtils.convertKeyValuesToString<
            IJWPSponsorshipAdTargeting
        >(sponsorshipAdTag, '%3D', '%26')}`

        const adTargeting: IJWPVideoAdTargeting = {
            iu,
            ppid,
            description_url: '__page-url__',
            sz: this.convertPlayerSizesToString(playerAdSizes),
            cmsid: '2568351',
            vid: '__item-mediaid__',
            gdfp_req: '1',
            output: 'vast',
            unviewed_position_start: '1',
            env: 'vp',
            tfcd: '0',
            impl: 's',
            correlator: '',
            cust_params: `${custParams}${custSponsorshipParams}`
        }

        const videoAdTag = `${'https://pubads.g.doubleclick.net/gampad/ads'}?${ObjectUtils.convertKeyValuesToString<
            IJWPVideoAdTargeting
        >(adTargeting)}`

        return {
            client: 'googima',
            adscheduleid: adScheduleId || `${playerAdscheduleId}`,
            schedule: [
                {
                    offset: 'pre',
                    tag: videoAdTag,
                    type: 'linear'
                }
            ]
        }
    }

    /**
     * convert the nest array of player sizes to a string
     */
    private convertPlayerSizesToString(
        adSizes: number[][],
        delimiter = 'x',
        divider = '%7C'
    ): string {
        return adSizes
            .map((item: number[]) => `${item[0]}${delimiter}${item[1]}`)
            .join(`${divider}`)
    }

    // From Analytics
    /**
     * Reset Flags should be called with new video
     */
    public resetFlags(): void {
        this.playStarted = false
        this.previousState = State.NULL
    }

    /**
     * Handle the player error event
     *
     * @param event
     */
    public onError(event: jwplayer.ErrorParam): void {
        if (event.message) {
            this.dispatchAnalyticsError(`${event.message} code: ${event.code}`)
        }
    }

    /**
     * Handle monetization configuration
     * based on monetize metadata flag
     *
     * @param IJWMedia
     */
    public isMonetize(data: IJWMedia): boolean {
        return data &&
            data['playlist'] &&
            data['playlist'][0]['monetize'] === 'false'
            ? false
            : true
    }

    /**
     * Get media metadata
     *
     * https://developer.jwplayer.com/jwplayer/reference/get_v2-media-media-id
     * @param id, ex: wYiMWUhb
     */
    private async getJWMediaMetadata(id: string): Promise<IJWMedia | void> {
        return fetch(`https://cdn.jwplayer.com/v2/media/${id}`)
            .then(async (res) => {
                return res.json()
            })
            .catch((err) => console.error(err))
    }
}
